/**
 * Responsive-image candidate building for Strapi-hosted media.
 *
 * Strapi's upload plugin already generates `thumbnail` (245w), `small` (500w),
 * `medium` (750w) and `large` (1000w) copies of every upload
 * (`bt-strapi/node_modules/strapi-plugin-upload/services/image-manipulation.js:91-95`)
 * and puts them in the `formats` map of every media object in the CMS payload.
 * The storefront used to discard all of them and ship the original — 818,982 bytes
 * for the home carousel's LCP banner where a 52,208-byte variant covers the slot.
 *
 * This module is deliberately pure: no React, no DOM, no HTTP, no `req`. It is the
 * unit that can be tested without a browser (root CLAUDE.md standard 2), which
 * matters here because a wrongly chosen candidate renders *identically* and is
 * invisible to a visual check — it can only be caught by asserting the filename.
 *
 * See `work/21b-image-weight/design.md` §5.2(a).
 */

/** The bucket host that some CMS rows still carry; requests to it bypass CloudFront. */
const S3_HOST_PREFIX = 's3.ap-southeast-1.amazonaws.com/';

/**
 * Anchored at the end of the path so a *directory* or basename containing "png"/"jpg"
 * is not corrupted. Copied from `components/common/WebPImage.js:82`, including the
 * query-string group — the naive `src.replace(ext, 'webp')` this replaces was a bug.
 */
const WEBP_CONVERTIBLE = /\.(jpg|jpeg|png|gif|bmp|tiff)(\?.*)?$/i;

/**
 * Widths Strapi emits for responsive delivery, ascending. `thumbnail` is deliberately
 * NOT in this list: it is a 245w crop-preview and is only used when it is the *only*
 * format present (images narrower than the 500px breakpoint — e.g. the 16 category
 * icons at 271x313 — get a thumbnail and nothing else).
 */
const RESPONSIVE_KEYS = ['small', 'medium', 'large'];

/**
 * The 1x display width of each slot, read from the SCSS that sizes it — not guessed.
 * `width` feeds the 1x-coverage rule below; `sizes` is the attribute the browser uses.
 *
 * These literals hard-code breakpoints that live in SCSS and will desync silently the
 * first time someone edits one. `strapi.scss:5-8` (350/250px tall, 768px breakpoint),
 * `strapi.scss:15-18` (`img { width: 1920px }` / `768px` — a fixed px width, so the
 * carousel is cropped, not scaled), `vendor/bootstrap/bootstrap.css:624-628`
 * (`.container` max-width 1140 at >=1200px).
 *
 * @type {Object<string, { width: number, sizes: string }>}
 */
export const IMAGE_SLOTS = {
    /** Home/page carousel above 768px — `strapi.scss:15`. */
    CAROUSEL_DESKTOP: { width: 1920, sizes: '1920px' },
    /**
     * Home/page carousel at <=768px — `strapi.scss:16-18`.
     * Its own literal, NOT a shared `(max-width: 768px) 768px, 1920px` string: the
     * 1x-coverage rule is evaluated per `<source>`, and evaluating it against the
     * 1920 in a shared string re-admits the 6400w original on the LCP element.
     * See qc-design.md N-1.
     */
    CAROUSEL_MOBILE: { width: 768, sizes: '768px' },
    /** Full-width sub-banner in `.container > .row > .col-12` — Grid1. */
    GRID1_DESKTOP: { width: 1140, sizes: '(max-width: 1199px) 100vw, 1140px' },
    /** Same cell under ImageWebP's portrait-phone `<source>`: at most a 767px viewport, less 30px of container padding. */
    GRID1_MOBILE: { width: 737, sizes: 'calc(100vw - 30px)' },
    /** Half-width sub-banner in `.col-6` — Grid2. */
    GRID2_DESKTOP: { width: 570, sizes: '(max-width: 1199px) 50vw, 570px' },
    /** Half of the Grid1 mobile slot. */
    GRID2_MOBILE: { width: 368, sizes: 'calc(50vw - 15px)' },
};

/**
 * Strip the raw-bucket host so the request goes through CloudFront.
 *
 * Preserved from `util.js:952-953`; at least one live call site depends on it
 * (`views/KoreaMain.js:257` passes an `s3.ap-southeast-1.amazonaws.com/...` URL).
 * Dropping it does not break anything visibly — the origin answers 200 — it just
 * silently bypasses the CDN. See qc-design.md N-7.
 *
 * @param {string} url - Any media URL.
 * @returns {string} The same URL against the CDN host.
 */
export function normalizeMediaUrl(url) {
    if (!url || typeof url !== 'string') return '';
    return url.replace(S3_HOST_PREFIX, '');
}

/**
 * Swap a raster extension for `.webp`, anchored at the end of the path.
 *
 * The `.webp` twins are produced by a process that lives in no repository we hold
 * (design.md §9), so the result of this function is only ever offered inside a
 * `<source type="image/webp">` where the browser can fall back — never as the `src`.
 *
 * @param {string} url - Any media URL.
 * @returns {string} The `.webp` sibling URL, or the input unchanged when the
 *   extension is not convertible (including a URL that is already `.webp`).
 */
export function toWebpUrl(url) {
    const normalized = normalizeMediaUrl(url);
    if (!normalized) return '';
    return normalized.replace(WEBP_CONVERTIBLE, '.webp$2');
}

/**
 * Collect the usable `formats.*` entries, ascending by width.
 *
 * @param {Object} formats - Strapi `formats` map; may be partial or absent.
 * @returns {Array<{ url: string, width: number }>} Candidates, possibly empty.
 */
function collectVariants(formats) {
    if (!formats || typeof formats !== 'object') return [];
    const variants = RESPONSIVE_KEYS
        .map((key) => formats[key])
        .filter((format) => format && format.url && format.width > 0)
        .map(({ url, width }) => ({ url, width }));
    if (variants.length > 0) return variants.sort((a, b) => a.width - b.width);
    // Only reached for images uploaded below the 500px breakpoint, which get a
    // thumbnail and nothing else (16 of the 97 live homepage objects).
    const { thumbnail } = formats;
    if (thumbnail && thumbnail.url && thumbnail.width > 0) {
        return [{ url: thumbnail.url, width: thumbnail.width }];
    }
    return [];
}

/**
 * Build the `srcset` candidate list for one `<source>`/`<img>`.
 *
 * **The 1x-coverage rule.** The original is added as a candidate only if no
 * `formats.*` variant is >= the slot's own 1x width. It is never added merely to
 * satisfy a DPR multiplier: when no candidate meets the requested density the
 * browser takes the largest available, which is what turns the 768px carousel slot
 * from 818,982 bytes into 52,208 at DPR 1, 2 and 3 alike. Offering the 6400w
 * original "for retina" reconstitutes the whole defect (design.md §5.2(a), F-1).
 *
 * `slotWidth` is the effective 1x width **under this element's own media
 * condition** — not the largest number in a shared `sizes` string. A single
 * `srcset` shared across a mobile and a desktop `<source>` reintroduces F-1.
 *
 * **Intrinsic dimensions.** `width`/`height` are echoed back untouched when the media
 * object carried both. They are not used to choose a candidate — every Strapi variant of
 * an upload has the same aspect ratio as its original, so one pair describes the whole
 * candidate list — they exist so the renderer can reserve the box before the bytes
 * arrive (design.md §5.2(d), the CLS work). A call site that passes a URL string, or an
 * object without `height`, gets `undefined` for both and renders exactly as it does today.
 *
 * @param {Object} params
 * @param {string} params.url - The original media URL.
 * @param {Object} [params.formats] - Strapi `formats` map; partial or absent is normal.
 * @param {number} [params.width] - Intrinsic width of the original, from the media object.
 * @param {number} [params.height] - Intrinsic height of the original, from the media object.
 * @param {number} [params.slotWidth] - 1x CSS width of the slot this element fills.
 *   Omit it to opt out of `srcset` entirely (the 30-odd legacy call sites do).
 * @returns {{ src: string, webpSrc: string, srcSet: (string|undefined), webpSrcSet: (string|undefined),
 *   width: (number|undefined), height: (number|undefined) }}
 *   `srcSet`/`webpSrcSet` are `undefined` — never an empty string — when there is
 *   nothing to offer, so React omits the attribute. `width`/`height` are `undefined`
 *   unless *both* are usable; a lone dimension is worse than none, because half a pair
 *   is not an aspect ratio.
 */
export function buildSrcSet({ url, formats, width, height, slotWidth }) {
    const src = normalizeMediaUrl(url);
    const webpSrc = toWebpUrl(url);
    const intrinsic = width > 0 && height > 0 ? { width, height } : { width: undefined, height: undefined };
    if (!src || !slotWidth) return { src, webpSrc, srcSet: undefined, webpSrcSet: undefined, ...intrinsic };

    const candidates = collectVariants(formats);
    const widest = candidates.length > 0 ? candidates[candidates.length - 1].width : 0;

    // Guarantee 1x coverage; never pay for more than the variants can give.
    if (widest < slotWidth) {
        // Without an intrinsic width we cannot write a `w` descriptor for the original,
        // and a candidate list that cannot cover the slot would upscale. Fall back to
        // today's behaviour (single `src`) rather than ship a wrong candidate.
        if (!(width > 0)) return { src, webpSrc, srcSet: undefined, webpSrcSet: undefined, ...intrinsic };
        candidates.push({ url: src, width });
    }
    if (candidates.length === 0) return { src, webpSrc, srcSet: undefined, webpSrcSet: undefined, ...intrinsic };

    const descriptors = candidates.map((candidate) => ({
        url: normalizeMediaUrl(candidate.url),
        webpUrl: toWebpUrl(candidate.url),
        width: candidate.width,
    }));
    return {
        src,
        webpSrc,
        srcSet: descriptors.map((d) => `${d.url} ${d.width}w`).join(', '),
        webpSrcSet: descriptors.map((d) => `${d.webpUrl} ${d.width}w`).join(', '),
        ...intrinsic,
    };
}
